import { Navigate, useNavigate } from "@solidjs/router";
import { BiSolidUserAccount } from "solid-icons/bi";
import {
  IoArrowBack,
  IoBusinessOutline,
  IoInformationCircleOutline,
} from "solid-icons/io";
import { createSignal, type JSX, Match, Show, Switch } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/ui/loading";
import {
  AuthDescription,
  AuthFormBox,
  AuthHeader,
  AuthTitle,
} from "@/domains/auth/components/AuthBoxes";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { useStartPublishingAsFreelancer } from "@/domains/company/company/hooks/useStartPublishingAsFreelancer";

/**
 * Entry route for users who want to publish an offer from outside a publisher
 * workspace. Providers keep going directly to the job offer flow; candidates
 * choose whether to publish as a solo freelancer or create a real company.
 */
export default function JobOfferCreateRedirectRoute() {
  const [auth] = useAuthV2();

  const isProvider = () => auth?.capabilities?.can_publish === true;

  return (
    <Show when={!auth?.loading} fallback={<Loading />}>
      <Switch>
        <Match when={isProvider()}>
          <Navigate href="/job-offers/create/ai" />
        </Match>
        <Match when={!isProvider()}>
          <PublisherWorkspaceChoice />
        </Match>
      </Switch>
    </Show>
  );
}

const PublisherWorkspaceChoice = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [selectedKind, setSelectedKind] = createSignal<
    "company" | "freelancer" | null
  >(null);
  const { startPublishingAsFreelancer, isPending } =
    useStartPublishingAsFreelancer();

  const continueWithSelection = () => {
    switch (selectedKind()) {
      case "company":
        navigate("/company/create");
        return;
      case "freelancer":
        void startPublishingAsFreelancer({ replace: true });
        return;
    }
  };

  return (
    <main class="min-h-screen bg-white px-6 py-12 md:px-10 md:py-20">
      <MetaTitleWithSeparator>
        {t("jobOffers.create.workspaceChoice.title")}
      </MetaTitleWithSeparator>

      <AuthFormBox class="mx-auto w-full max-w-5xl gap-10">
        <AuthHeader class="max-w-4xl">
          <AuthTitle class="text-4xl font-semibold md:text-5xl">
            {t("jobOffers.create.workspaceChoice.title")}
          </AuthTitle>
          <AuthDescription class="text-xl leading-8">
            {t("jobOffers.create.workspaceChoice.description")}
          </AuthDescription>
        </AuthHeader>

        <div class="grid gap-4 md:grid-cols-2">
          <WorkspaceChoiceCard
            title={t("jobOffers.create.workspaceChoice.company.title")}
            description={t(
              "jobOffers.create.workspaceChoice.company.description",
            )}
            icon="company"
            selected={selectedKind() === "company"}
            onClick={() => setSelectedKind("company")}
          >
            <div class="mt-4 flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
              <IoInformationCircleOutline class="mt-0.5 size-4 shrink-0" />
              <p>{t("jobOffers.create.workspaceChoice.company.warning")}</p>
            </div>
          </WorkspaceChoiceCard>

          <WorkspaceChoiceCard
            title={t("jobOffers.create.workspaceChoice.freelancer.title")}
            description={t(
              "jobOffers.create.workspaceChoice.freelancer.description",
            )}
            icon="freelancer"
            selected={selectedKind() === "freelancer"}
            onClick={() => setSelectedKind("freelancer")}
            disabled={isPending()}
          />
        </div>

        <div class="flex justify-between">
          <Button variant="outline" onClick={() => navigate("/profile")}>
            <IoArrowBack size={16} class="mr-2" />
            {t("common.back")}
          </Button>
          <Button
            size="lg"
            onClick={continueWithSelection}
            disabled={!selectedKind() || isPending()}
          >
            {isPending() ? "..." : t("common.continue")}
          </Button>
        </div>
      </AuthFormBox>
    </main>
  );
};

const WorkspaceChoiceCard = (props: {
  title: string;
  description: string;
  icon: "company" | "freelancer";
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  children?: JSX.Element;
}) => {
  const Icon = () => {
    switch (props.icon) {
      case "company":
        return <IoBusinessOutline class="size-8 text-primary" />;
      case "freelancer":
        return <BiSolidUserAccount class="size-8 text-amber-700" />;
    }
  };

  return (
    <button
      type="button"
      class="relative flex min-h-56 flex-col items-start rounded-lg border bg-white p-5 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-70"
      classList={{
        "border-primary bg-primary/5": props.selected,
        "border-border": !props.selected,
      }}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      <div
        class="mb-6 flex size-12 items-center justify-center rounded-lg"
        classList={{
          "bg-primary/10": props.icon === "company",
          "bg-amber-100": props.icon === "freelancer",
        }}
      >
        <Icon />
      </div>
      <div
        class="absolute right-5 top-5 size-7 rounded-full border-2"
        classList={{
          "border-primary bg-primary": props.selected,
          "border-muted-foreground/30": !props.selected,
        }}
      />
      <div class="flex flex-col items-start pr-8">
        <p class="text-lg font-semibold leading-snug text-foreground">
          {props.title}
        </p>
        <p class="mt-2 text-sm leading-6 text-muted-foreground">
          {props.description}
        </p>
      </div>
      {props.children}
    </button>
  );
};
